/**
 * The artwork behind the welcome screen's brand moment: hand-cut paper blocks,
 * each carrying a sketched glyph for one surface a user actually works in -
 * correspondence, tasks, drafting, conversation, constituents, search,
 * approval, send - with the real Pulse mark holding the centre as the
 * keystone. Tile edges are deliberately irregular (wavy cubic edges, per-tile
 * wobble) and glyph strokes carry small hand-drawn deviations - precision
 * would read as clipart; the imperfection is the refinement.
 *
 * Data only. `pulse-blocks.tsx` renders it and owns the motion.
 */

import { PULSE_MARK_PATH } from './pulse-mark-path';

import type { ReactNode } from 'react';

const STROKE = '#2A2356';

const G = {
  stroke: STROKE,
  strokeWidth: 6.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  fill: 'none',
};

/**
 * A hand-cut tile: rounded-corner quad whose edges bow in or out by a few
 * units. Offsets are fixed per tile (deterministic art, no randomness).
 * e = [top, right, bottom, left] edge bulge; positive bows outward.
 */
function wavyTile(e: readonly [number, number, number, number]): string {
  const s = 100;
  const r = 12;
  const [et, er, eb, el] = e;
  return [
    `M ${r} 0`,
    `C ${s * 0.33} ${-et} ${s * 0.67} ${et} ${s - r} 0`,
    `Q ${s} 0 ${s} ${r}`,
    `C ${s + er} ${s * 0.33} ${s - er} ${s * 0.67} ${s} ${s - r}`,
    `Q ${s} ${s} ${s - r} ${s}`,
    `C ${s * 0.67} ${s + eb} ${s * 0.33} ${s - eb} ${r} ${s}`,
    `Q 0 ${s} 0 ${s - r}`,
    `C ${-el} ${s * 0.67} ${el} ${s * 0.33} 0 ${r}`,
    `Q 0 0 ${r} 0`,
    'Z',
  ].join(' ');
}

interface TileSpec {
  readonly col: 0 | 1 | 2;
  readonly row: 0 | 1 | 2;
  readonly tilt: number;
  readonly fill: string;
  readonly edges: readonly [number, number, number, number];
  readonly glyph: ReactNode;
}

/**
 * The folded dart, drawn once and shared. The tile and the flying copy render
 * this same fragment at the same scale and stroke, so the plane that lifts off
 * is visibly the plane that was sitting in the square - not a lookalike that
 * can drift from it on the next edit.
 */
function SendPlaneArt() {
  return (
    <>
      <path d="M74.5 27 C59.5 33.5 42.5 41.5 25.5 49 C33 52 39 54.5 45.5 56.5 C47.5 63 49.5 68.5 51.5 75 Z" />
      <path d="M74.5 27 L45.5 56.5" />
    </>
  );
}

/* Glyphs live in a 100x100 tile-local space; paths carry deliberate wobble. */
const TILES: readonly TileSpec[] = [
  {
    // Envelope - correspondence in.
    col: 0,
    row: 0,
    tilt: -2.5,
    fill: '#F6BA4F',
    edges: [3, -2.5, 2.5, 3.5],
    glyph: (
      <g {...G}>
        <path d="M25 34 L75 31.5 L76.5 66 L24 68 Z" />
        <path d="M27.5 36 L50.5 54.5 L73 33.5" />
      </g>
    ),
  },
  {
    // Clipboard - tasks. Board plus two ruled lines, no tick: the tick tile
    // owns approval and the two must not read as the same idea.
    col: 1,
    row: 0,
    tilt: 1.5,
    fill: '#8BCB90',
    edges: [-3, 2.5, 3, -2],
    glyph: (
      <g {...G}>
        <path d="M32 32 L68 30 L70 72 L34.5 73.5 Z" />
        <path d="M43 26.5 L58.5 25.5 L59 34 L43.5 34.5 Z" />
        <path d="M42.5 48 L61.5 47" />
        <path d="M43 59.5 L57 58.5" />
      </g>
    ),
  },
  {
    // Pen - drafting.
    col: 2,
    row: 0,
    tilt: -2,
    fill: '#F6BA4F',
    edges: [2.5, 3, -3, -2],
    glyph: (
      <g {...G}>
        <path d="M32.5 68 L36 51 L61.5 24.5 L75 38.5 L49.5 64.5 Z" />
        <path d="M36.5 51.5 L49 63.5" />
      </g>
    ),
  },
  {
    // Speech bubble - the conversation on a correspondence.
    col: 0,
    row: 1,
    tilt: 1,
    fill: '#8BCB90',
    edges: [3.5, -2, 2, -3],
    glyph: (
      <g {...G}>
        <path d="M29 60 C26 47 33.5 33.5 51 32 C65 31 74.5 40 73.5 51 C72.5 62 62 68.5 50 67.5 L45 67 L33 75 L36.5 65" />
      </g>
    ),
  },
  {
    // Constituent - the person on the other end of every thread.
    col: 2,
    row: 1,
    tilt: 2,
    fill: '#8BCB90',
    edges: [-2.5, 3, 2.5, 2],
    glyph: (
      <g {...G}>
        <path d="M50 25.5 C56.5 25.5 61.5 30.5 61.5 37 C61.5 43.5 56.5 48.5 50 48.5 C43.5 48.5 38.5 43.5 38.5 37 C38.5 30.5 43.5 25.5 50 25.5 Z" />
        <path d="M29.5 73.5 C30.5 63 38.5 56.5 50 56.5 C61.5 56.5 69.5 63 70.5 73.5" />
      </g>
    ),
  },
  {
    // Magnifier - knowledge search. Circle drawn open like a sketch pass.
    col: 0,
    row: 2,
    tilt: 2.5,
    fill: '#C9ACF4',
    edges: [-2, 2.5, 3, -3.5],
    glyph: (
      <g {...G}>
        <path d="M52 30 C60 33 64 41 61 49.5 C58 57.5 49 61 41.5 57.5 C34 54 30.5 45.5 34 38 C37 31.5 44.5 28.5 51 30.5" />
        <path d="M59.5 57 L73.5 72" />
      </g>
    ),
  },
  {
    // Tick - approval.
    col: 1,
    row: 2,
    tilt: -1.5,
    fill: '#F6BA4F',
    edges: [2, -3, -2.5, 3],
    glyph: (
      <g {...G}>
        <path d="M29 53 C36 58 41 63 44.5 67 C52 55 61 43 72.5 34.5" />
      </g>
    ),
  },
  {
    // Paper plane - send. Folded dart with a hand-drawn keel. This is the one
    // tile that tells a story rather than naming a surface: the plane leaves
    // and a tick arrives, which is the whole product in one gesture. The two
    // glyphs are stacked and cross-faded by CSS, never mounted conditionally,
    // so the loop costs no renders.
    col: 2,
    row: 2,
    tilt: -2,
    fill: '#C9ACF4',
    edges: [-3.5, 2, 2.5, -2.5],
    glyph: (
      <>
        <g {...G} className="pp-send-depart">
          <SendPlaneArt />
        </g>
        {/* Ringed, unlike the bare tick two tiles over: that one is a decision
            (approved), this one is a receipt (delivered). Identical marks
            sitting side by side would read as the same idea twice. */}
        <g {...G} className="pp-send-land">
          <path
            d="M50 23.5 C64.5 23.5 76.5 35.5 76.5 50 C76.5 64.5 64.5 76.5 50 76.5 C35.5 76.5 23.5 64.5 23.5 50 C23.5 36 34.5 24 48.5 23.5"
            strokeWidth={5.5}
          />
          <path d="M37.5 50 C41.5 53.5 44 56.5 46 59 C51 51.5 57 44.5 63.5 39" />
        </g>
      </>
    ),
  },
  {
    // The Pulse mark itself - the real favicon artwork, holding the centre as
    // the keystone the other eight surfaces sit around. Filled in the same ink
    // as every glyph stroke so the grid reads as one drawn system; the wave is
    // knocked out of the disc, so the tile colour shows through it.
    col: 1,
    row: 1,
    tilt: -1,
    fill: '#C9ACF4',
    edges: [-2.5, -3, 3, 2.5],
    glyph: (
      <g transform="translate(22 22) scale(0.1965)">
        <path d={PULSE_MARK_PATH} fill={STROKE} />
      </g>
    ),
  },
];

export const CELL = 116;
export const TILE = 100;
export const CANVAS = CELL * 2 + TILE;

/** Dead centre of the grid - the orbit's axis and the flight's destination. */
export const CENTRE = CANVAS / 2;

/**
 * The travelling plane: the same folded dart the send tile carries, drawn once
 * more for the flight around the grid.
 *
 * The circle is made by ROTATING A PARENT about the grid centre while the
 * plane sits out on a radius - so the path is a circle by construction, not a
 * polygon approximated from waypoints, which is what stepping between
 * keyframed positions gives you. The parent's rotation carries the plane's
 * heading with it, so it stays tangent for free.
 *
 * It renders the SAME shared artwork as the tile, at the same scale and
 * stroke, so the plane that lifts off is visibly the one that was sitting in
 * the square. Only the rotation differs: the art points along -Y in the orbit
 * frame (90deg BEFORE the +X radius) because tangent-to-an-anticlockwise-orbit
 * is perpendicular to the radius on that side, less the 43deg of the drawing's
 * own lean. Flip the orbit's direction and this MUST flip with it, or the
 * plane flies its circle tail-first.
 */
export function PulsePlane() {
  return (
    <g className="pp-plane-orbit">
      <g transform={`translate(${CENTRE} ${CENTRE})`}>
        <g className="pp-plane-radius">
          <g {...G} transform="rotate(-47) translate(-50 -50)">
            <SendPlaneArt />
          </g>
        </g>
      </g>
    </g>
  );
}

/** How long a single tile's fall takes; keep in step with `pp-block-drop`. */
export const DROP_DURATION_MS = 720;

/**
 * Scrambled landing order (index-aligned with TILES): no two consecutive
 * landings are grid neighbours, so the fill never reads as a sweep, and the
 * centre mark drops LAST - the keystone is the punchline.
 */
export const DROP_DELAYS_MS = [0, 130, 370, 310, 250, 190, 430, 65, 530] as const;

/**
 * Per-tile breathing periods (index-aligned with TILES). Deliberately
 * co-prime-ish and never equal to a neighbour's: shared periods make the grid
 * pulse as one slab, which reads mechanical. These drift apart instead.
 */
export const FLOAT_PERIODS_S = [5.4, 6.7, 4.9, 6.1, 5.7, 7.3, 5.1, 6.4, 5.9] as const;

export { TILES, wavyTile };
export type { TileSpec };
